import { generateBankQRCode, resolveBankBin } from '@tcr-erp/shared';
import type { BankAccount } from '@/services/settings.service';
import type { BankListItem } from '@/services/bank.service';

/**
 * TCR-1901 — chọn tài khoản nhận tiền để sinh QR ở chi tiết đơn.
 *
 * Trước fix, `OrderQrPayment` hardcode cứng một số tài khoản VietinBank trong
 * source nên mọi đơn của mọi chi nhánh đều ra đúng một mã quét. Giờ mỗi chi
 * nhánh thiết lập ngân hàng riêng (`bank_account.store_id`), nên phải tra theo
 * chi nhánh của đơn.
 *
 * Tách thành hàm thuần để thứ tự ưu tiên và mã ngân hàng có test riêng, không
 * phải dựng cả component mới kiểm được.
 */

/** Số tài khoản bị tắt không bao giờ được đem ra quét. */
function isUsable(account: BankAccount): boolean {
  return account.enabled !== false;
}

function belongsToStore(account: BankAccount, storeId: string): boolean {
  return account.store_id != null && String(account.store_id) === storeId;
}

/** Bỏ trống `store_id` = dùng chung cho mọi chi nhánh. */
function isShared(account: BankAccount): boolean {
  return account.store_id == null;
}

/** Mặc định trước, rồi giữ nguyên thứ tự BE trả về (đã sort theo created_at). */
function defaultFirst(accounts: BankAccount[]): BankAccount[] {
  return [
    ...accounts.filter((a) => a.is_default),
    ...accounts.filter((a) => !a.is_default),
  ];
}

/**
 * Danh sách tài khoản được phép chọn cho đơn, theo đúng thứ tự ưu tiên:
 *
 * 1. Tài khoản gán đúng chi nhánh của đơn — có cái nào thì DỪNG ở đây
 * 2. Chi nhánh chưa gán tài khoản riêng → tài khoản dùng chung
 * 3. Không có cả hai → danh sách rỗng, panel báo "chưa thiết lập" kèm link sang
 *    trang cài đặt.
 *
 * Tài khoản của chi nhánh KHÁC không bao giờ lọt vào danh sách: thu ngân chi
 * nhánh này không được vô tình đưa khách quét vào tài khoản chi nhánh kia.
 *
 * QA reopen 18/09 (lượt 2): bản đầu gộp cả tài khoản riêng lẫn dùng chung vào
 * một danh sách nên chi nhánh đã cấu hình ngân hàng riêng vẫn thấy thêm lựa
 * chọn dùng chung. Chi nhánh đã chọn ngân hàng của mình thì đó là mã QR duy nhất.
 *
 * QA reopen 18/09 (lượt 3, Bug2): bỏ nốt nhánh 3 cũ — "rơi về tài khoản mặc
 * định của tenant kể cả khi nó gán cho chi nhánh khác". Nhánh đó là đường duy
 * nhất còn lại đưa tiền của chi nhánh A chạy vào tài khoản chi nhánh B, và nó
 * im lặng: màn hình vẫn vẽ ra mã QR trông bình thường. Tài khoản dùng chung giờ
 * bị khoá không cho tắt hết (xem `isLastEnabledSharedAccount`), nên lưới an
 * toàn nằm ở bước 2 chứ không phải ở nhánh đoán mò này.
 */
export function selectOrderBankAccounts(
  accounts: BankAccount[] | undefined,
  storeId?: string | null,
): BankAccount[] {
  const usable = (accounts ?? []).filter(isUsable);
  if (usable.length === 0) return [];

  const ofStore = storeId
    ? usable.filter((a) => belongsToStore(a, String(storeId)))
    : [];
  if (ofStore.length > 0) return defaultFirst(ofStore);

  const shared = usable.filter(isShared);
  if (shared.length > 0) return defaultFirst(shared);

  return [];
}

/**
 * BIN Napas của tài khoản, để sinh chuỗi VietQR.
 *
 * Ưu tiên `bank_code` đã lưu trên tài khoản; tài khoản cũ chưa có thì tra ngược
 * qua master data `banks` để lấy tên viết tắt rồi map sang BIN.
 *
 * KHÔNG dùng cột `banks.bin`: giá trị trong DB là số 3 chữ số (Vietcombank =
 * `254`) chứ không phải BIN thật `970436`, quét ra sẽ trỏ nhầm ngân hàng.
 */
export function resolveAccountBankBin(
  account: Pick<BankAccount, 'bank_code' | 'bank_name'>,
  banks: BankListItem[] = [],
): string | null {
  const name = account.bank_name?.trim().toLowerCase();
  const matched = name
    ? banks.find((b) => b.name?.trim().toLowerCase() === name)
    : undefined;

  return resolveBankBin(
    account.bank_code,
    matched?.short_name,
    matched?.code,
    account.bank_name,
  );
}

export interface OrderQrPayloadParams {
  bin: string;
  accountNumber: string;
  amount: number;
  content: string;
}

/**
 * Chuỗi VietQR chuẩn EMVCo để vẽ mã quét.
 *
 * Bản cũ nhét URL ảnh `img.vietqr.io/...` vào `qrcode.toDataURL()` — máy quét
 * đọc ra một đường link chứ không phải lệnh chuyển tiền, nên mã "đúng ngân
 * hàng" vẫn vô dụng khi khách quét bằng app ngân hàng. Dùng lại đúng helper
 * `generateBankQRCode` mà luồng billing đang chạy thật.
 */
export function buildOrderQrPayload({
  bin,
  accountNumber,
  amount,
  content,
}: OrderQrPayloadParams): string {
  return generateBankQRCode(
    bin,
    accountNumber,
    amount > 0 ? String(Math.round(amount)) : '',
    content,
  );
}
